import { useNavigate } from 'react-router-dom';
import {
  Brain, Calendar, BookOpen, Shield, TrendingUp,
  Link, Mail, Video, Layout, ArrowRight, Zap, Lightbulb, FlaskConical
} from 'lucide-react';

const LAB_CARDS = [
  {
    title: 'Brainstorm Lab',
    subtitle: '4-Round Escalating AI Council',
    description: 'GPT-4o · Gemini · Perplexity · Groq debate your idea across 4 escalating rounds. Claude Sonnet 4.6 synthesizes the final blueprint.',
    icon: Lightbulb,
    gradient: 'linear-gradient(135deg, #ff5370, #7c5efa)',
    glow: 'rgba(255,83,112,0.4)',
    tag: 'Initial → Stress Test → Synthesis → Blueprint',
    path: '/brainstorm',
    badge: 'NEW',
  },
  {
    title: 'Research Lab',
    subtitle: '8-Phase Autonomous Research',
    description: 'Discovers real literature from Semantic Scholar + arXiv. Drafts, peer-reviews, quality-gates, and delivers a final content blueprint.',
    icon: FlaskConical,
    gradient: 'linear-gradient(135deg, #3dffa0, #00d4b8)',
    glow: 'rgba(61,255,160,0.4)',
    tag: 'Literature · Synthesis · Peer Review · Blueprint',
    path: '/research',
    badge: 'NEW',
  },
];

const CARDS = [
  {
    title: 'Content Calendar',
    subtitle: 'Visual Weekly Planner',
    description: 'AI-powered 7-day calendar with 7:30 AM ET default. 80/20 topic balance. 3-day advance drafting.',
    icon: Calendar,
    gradient: 'linear-gradient(135deg, #7c5efa, #2193b0)',
    glow: 'rgba(124,94,250,0.4)',
    tag: '7:30 AM ET · Auto-Draft',
    path: '/calendar',
  },
  {
    title: 'Blog Publisher',
    subtitle: 'SEO · AIO · LLMO Optimized',
    description: 'Publish optimized blogs to qlia.io/blogs and iconiqs.ai/blogs. Auto-generates social posts.',
    icon: BookOpen,
    gradient: 'linear-gradient(135deg, #2193b0, #6dd5ed)',
    glow: 'rgba(33,147,176,0.4)',
    tag: 'qlia.io · iconiqs.ai · LinkedIn',
    path: '/blog',
  },
  {
    title: 'Preview Board',
    subtitle: 'Kanban Pipeline',
    description: 'Bird\'s-eye view of your content pipeline. Drag from Draft → Pending → Approved → Published.',
    icon: Layout,
    gradient: 'linear-gradient(135deg, #f0c27f, #fc5c7d)',
    glow: 'rgba(240,194,127,0.4)',
    tag: 'Draft · Pending · Approved · Published',
    path: '/board',
    badge: 'NEW',
  },
  {
    title: 'Competitive Intel',
    subtitle: 'Battlefield Intelligence',
    description: 'Map competitor content strategies, find untapped gaps, and get AI-powered strategic moves.',
    icon: Shield,
    gradient: 'linear-gradient(135deg, #ff5370, #b71c1c)',
    glow: 'rgba(255,83,112,0.4)',
    tag: 'Content Gaps · AI Recommendations',
    path: '/intel',
  },
  {
    title: 'Performance Intelligence',
    subtitle: 'Autoresearch Feedback Loop',
    description: 'Karpathy-inspired loop: measure → identify patterns → generate learnings → improve content.',
    icon: TrendingUp,
    gradient: 'linear-gradient(135deg, #00d4b8, #1de9b6)',
    glow: 'rgba(0,212,184,0.4)',
    tag: 'Engagement Loop · Skill Ledger',
    path: '/performance',
  },
  {
    title: 'Traffic Converter',
    subtitle: 'Curiosity-Gap Click Machine',
    description: 'Turn any blog into 8 social variants using psychological hooks. Maximize clicks to qlia.io.',
    icon: Link,
    gradient: 'linear-gradient(135deg, #2193b0, #6dd5ed)',
    glow: 'rgba(33,147,176,0.4)',
    tag: '8 Hook Styles · A/B Plan',
    path: '/converter',
  },
  {
    title: 'Weekly Newsletter',
    subtitle: 'AI-Compiled Email Digest',
    description: 'Auto-compiles weekly content into branded HTML newsletter. Sent via Resend to subscribers.',
    icon: Mail,
    gradient: 'linear-gradient(135deg, #f0c27f, #fc5c7d)',
    glow: 'rgba(240,194,127,0.4)',
    tag: 'Auto-Compile · Resend',
    path: '/newsletter',
  },
  {
    title: 'Social Video Creator',
    subtitle: 'Remotion-Powered Animations',
    description: 'Create animated social videos for TikTok (9:16), Instagram (1:1), and Facebook (16:9).',
    icon: Video,
    gradient: 'linear-gradient(135deg, #fe2c55, #ff8a65)',
    glow: 'rgba(254,44,85,0.4)',
    tag: 'TikTok · Instagram · Remotion',
    path: '/video',
  },
];

export default function LaunchScreen() {
  const navigate = useNavigate();

  return (
    <div className="h-full overflow-y-auto" style={{ background: 'var(--llmc-bg)' }}>
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #7c5efa, #00d4b8)' }}>
              <Zap size={24} className="text-white" />
            </div>
          </div>
          <h1 className="text-5xl font-black mb-2 gradient-text" style={{ letterSpacing: '-0.04em' }}>
            IdeaWorks
          </h1>
          <p className="text-base mb-5" style={{ color: 'var(--llmc-text-muted)', maxWidth: 480, margin: '0 auto 20px' }}>
            AI-powered content engine for qlia.io & iconiqs.ai. Let Einstein orchestrate everything.
          </p>

          {/* Sid Hero CTA */}
          <button
            onClick={() => navigate('/strategist')}
            data-testid="chat-with-sid-btn"
            className="inline-flex items-center gap-2.5 px-6 py-3 rounded-xl font-bold text-white text-[15px] transition-all duration-200"
            style={{
              background: 'linear-gradient(135deg, #7c5efa, #00d4b8)',
              boxShadow: '0 8px 32px rgba(124,94,250,0.35)',
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 12px 40px rgba(124,94,250,0.5)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = '0 8px 32px rgba(124,94,250,0.35)'}
          >
            <Brain size={20} />
            Chat with Einstein — Your AI Strategist
            <ArrowRight size={16} />
          </button>
          <p className="text-xs mt-2" style={{ color: 'var(--llmc-text-muted)' }}>
            Einstein can orchestrate everything on this platform.
          </p>
        </div>

        {/* Labs Cards */}
        <div className="mb-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>AI Labs</div>
            <div className="flex-1 h-[1px]" style={{ background: 'var(--llmc-border)' }} />
            <div className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(61,255,160,0.1)', border: '1px solid rgba(61,255,160,0.3)', color: '#3dffa0' }}>NEW IN v3.0</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {LAB_CARDS.map((card) => {
              const Icon = card.icon;
              return (
                <button
                  key={card.path}
                  onClick={() => navigate(card.path)}
                  data-testid={`card-${card.path.replace('/', '')}`}
                  className="text-left llmc-card p-4 flex flex-col gap-3 cursor-pointer group relative overflow-hidden"
                  style={{ transition: 'all 0.25s ease' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = `0 12px 40px ${card.glow.replace('0.4', '0.15')}`;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = '';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  <div className="absolute top-0 left-0 right-0 h-[3px] rounded-t-xl opacity-80 group-hover:opacity-100 transition-opacity"
                    style={{ background: card.gradient }} />
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: card.gradient }}>
                      <Icon size={22} className="text-white" />
                    </div>
                    <div className="text-[9px] font-bold px-2 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.08)', color: '#3dffa0' }}>NEW</div>
                  </div>
                  <div>
                    <div className="font-bold text-white mb-0.5">{card.title}</div>
                    <div className="text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>{card.subtitle}</div>
                    <div className="text-xs leading-relaxed" style={{ color: 'var(--llmc-text-muted)' }}>{card.description}</div>
                  </div>
                  <div className="text-[10px] font-semibold px-2 py-1 rounded-full self-start"
                    style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>
                    {card.tag}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Feature Cards */}
        <div className="mb-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>Content Platform</div>
            <div className="flex-1 h-[1px]" style={{ background: 'var(--llmc-border)' }} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <button
                key={card.path}
                onClick={() => navigate(card.path)}
                data-testid={`card-${card.path.replace('/', '')}`}
                className="text-left llmc-card p-4 flex flex-col gap-3 cursor-pointer group relative overflow-hidden"
                style={{ transition: 'all 0.25s ease' }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = `0 12px 40px ${card.glow.replace('0.4', '0.15')}`;
                  e.currentTarget.style.borderColor = card.glow.replace('rgba', 'rgba').replace('0.4)', '0.5)');
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = '';
                  e.currentTarget.style.boxShadow = '';
                  e.currentTarget.style.borderColor = '';
                }}
              >
                {/* Top border accent */}
                <div className="absolute top-0 left-0 right-0 h-[3px] rounded-t-xl opacity-70 group-hover:opacity-100 transition-opacity"
                  style={{ background: card.gradient }} />

                {/* Icon */}
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: card.gradient }}>
                  <Icon size={22} className="text-white" />
                </div>

                {/* Text */}
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="font-bold text-sm text-white">{card.title}</span>
                    {card.badge && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                        style={{ background: 'rgba(61,255,160,0.15)', color: '#3dffa0' }}>
                        {card.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-xs mb-2 font-medium" style={{ color: 'var(--llmc-text-muted)' }}>
                    {card.subtitle}
                  </p>
                  <p className="text-[11px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    {card.description}
                  </p>
                </div>

                {/* Tag */}
                <div className="text-[10px] font-medium px-2 py-1 rounded-full"
                  style={{ background: card.glow.replace('0.4)', '0.1)'), color: 'rgba(255,255,255,0.6)', border: `1px solid ${card.glow.replace('0.4)', '0.25)')}` }}>
                  {card.tag}
                </div>

                {/* Launch arrow */}
                <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight size={14} style={{ color: card.glow.replace('rgba(', 'rgb(').replace(/,[\s]*0\.\d+\)/, ')') }} />
                </div>
              </button>
            );
          })}
        </div>

        <p className="text-center text-[10px] mt-6" style={{ color: 'rgba(255,255,255,0.15)' }}>
          v3.0 — LLM Council: Claude Sonnet 4.6 · GPT-4o · Gemini 2.5 · Multi-Agent Architecture
        </p>
      </div>
    </div>
  );
}
